import { useEffect, useState } from 'react';
import { Button, Checkbox, Flex, Modal, Typography } from 'antd';
import { getAccessTokenFromLS } from '@martech-solution/utils/utils';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { IAnnouncement } from 'src/types/helpdesk/announcement.type';
import { useMutation, useQuery } from '@tanstack/react-query';
import { dismissAnnouncement, getAnnouncementByEmployee } from 'src/apis/helpdesk/announcement.api';
import './AnnouncementModal.scss';
import 'react-quill/dist/quill.snow.css';
import { decodeJWT } from 'src/utils/utils';

const { Title } = Typography;

export default function AnnouncementModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [connection, setConnection] = useState<HubConnection | undefined>(undefined);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState<IAnnouncement | null>(null);
  const [announcementQueue, setAnnouncementQueue] = useState<IAnnouncement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const announcementByEmployeeQuery = useQuery({
    queryKey: ['announcementByEmployeeQuery'],
    queryFn: getAnnouncementByEmployee,
    staleTime: 5 * 60 * 1000,
    enabled: !!accessToken
  });

  const dismissAnnouncementMutate = useMutation({
    mutationFn: () => {
      return dismissAnnouncement(announcement?.id || 0);
    }
  });

  useEffect(() => {
    const token = getAccessTokenFromLS();
    if (!token) return;

    const decodedToken = decodeJWT(token || '');
    const role = (decodedToken.role as string)?.toLowerCase();
    if (role !== 'support') {
      setAccessToken(token);
    }
  }, []);

  useEffect(() => {
    const apiEndpoint =
      process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_API_ENDPOINT_PROD
        : process.env.REACT_APP_API_ENDPOINT;
    const announcementEndpoint = `${apiEndpoint}/announcementHub`;

    if (accessToken) {
      const connect = new HubConnectionBuilder()
        .withUrl(String(announcementEndpoint), {
          accessTokenFactory: () => accessToken
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.None)
        .build();

      setConnection(connect);
    }
  }, [accessToken]);

  useEffect(() => {
    if (connection) {
      if (connection.state === 'Disconnected') {
        connection.start().then(() => {
          connection.on('ReceiveAnnouncement', (message: IAnnouncement) => {
            if (isOpen) {
              setAnnouncementQueue((prev) => [...prev, message]);
            } else {
              setAnnouncement(message);
              setIsOpen(true);
              setIsDismissed(false);
            }
          });
        });
      }
    }
  }, [connection, isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    if (isDismissed && announcement) {
      dismissAnnouncementMutate.mutate();
    }

    const nextIndex = currentIndex + 1;
    if (nextIndex < announcementQueue.length) {
      setTimeout(() => {
        setCurrentIndex(nextIndex);
        showAnnouncementAtIndex(announcementQueue, nextIndex);
      }, 500);
    } else {
      setAnnouncementQueue([]);
      setCurrentIndex(0);
    }
  };

  useEffect(() => {
    const activeAnnouncements = announcementByEmployeeQuery.data?.data;
    if (activeAnnouncements && activeAnnouncements.length > 0) {
      setAnnouncementQueue(activeAnnouncements);
      setCurrentIndex(0);
      showAnnouncementAtIndex(activeAnnouncements, 0);
    }
  }, [announcementByEmployeeQuery.data?.data]);

  const showAnnouncementAtIndex = (queue: IAnnouncement[], index: number) => {
    if (index >= 0 && index < queue.length) {
      setAnnouncement(queue[index]);
      setIsOpen(true);
      setIsDismissed(false);
    }
  };

  return (
    <Modal
      title={
        <Flex align='center' justify='space-between' gap={12}>
          <Title level={4} className='announcement-title'>
            {announcement?.title || 'Thông báo'}
          </Title>
          {announcementQueue.length > 1 && (
            <span className='announcement-title--index'>
              {currentIndex + 1} / {announcementQueue.length}
            </span>
          )}
        </Flex>
      }
      open={isOpen}
      onOk={handleClose}
      onCancel={handleClose}
      footer={
        <Flex justify='space-between' align='center'>
          <Checkbox
            checked={isDismissed}
            onChange={(e) => {
              setIsDismissed(e.target.checked);
            }}
          >
            Không hiển thị nữa
          </Checkbox>
          <Button onClick={handleClose}>Đóng</Button>
        </Flex>
      }
      width={700}
      centered
    >
      <div
        className='announcement-content ql-editor'
        dangerouslySetInnerHTML={{
          __html: announcement?.content || 'Nội dung thông báo'
        }}
      />
    </Modal>
  );
}
